import { useState, useRef, useEffect } from "react";
import { ScanLine, Package, User, Phone, MapPin, Clock, Calendar, CheckCircle } from "lucide-react";
import { BsQrCodeScan } from "react-icons/bs";
import axios from "axios";
import { toast } from 'sonner';
import { BASE_URL } from '../../apiConfig';
import { statuses } from "../../cors/statusMap";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import successSound from "../../sounds/success.mp3";
import errorSound from "../../sounds/error.mp3";

export default function OrderScannerPage() {
  const { t } = useLanguage();
  const [barcode, setBarcode] = useState("")
  const [orders, setOrders] = useState([])
  const [scannedOrder, setScannedOrder] = useState(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  const { token } = useAuth();  

  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200";
  const getStatusText = (status) => statuses[status]?.text || status.replace('_', ' ');
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon;
    return Icon ? <Icon size={iconSize} className="mr-1" /> : null;
  };

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!scannedOrder) {
      inputRef.current?.focus()
    }
  }, [scannedOrder])

  const handleBarcodeChange = (e) => {
    const value = e.target.value
    setBarcode(value)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const scanOrder = async (code) => {
    if (!code || loading) return;

    const alreadyScanned = orders.some(o => o.code_api === code);
    if (alreadyScanned) {
      new Audio(errorSound).play();
      inputRef.current.value = "";
      toast.error(t('scan.already_scanned'))
      return; 
    }

    setLoading(true);
    try {
      const response = await axios.post(
        BASE_URL + "/api/scan-order",
        {
          code_api: code,
          status: "pre_a_ramasse",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.code === 200) {
        new Audio(successSound).play();
        const order = response.data.order
        
        const scannedDate = new Date().toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
        const scannedTime = new Date().toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })

        const existingOrderIndex = orders.findIndex(o => o.id === order.id);
        
        const historyEntry = {
          id: order.id,
          code: order.code,
          code_api: order.code_api,
          customer: order.client?.name || t('common.not_available'),
          items: order.products?.length || 0,
          total: `${order.total} DH`,
          status: order.status,
          scannedDate: scannedDate,
          scannedTime: scannedTime,
          fullOrder: order 
        }

        setScannedOrder(order)
        
        // Update orders list - add new or replace existing
        if (existingOrderIndex >= 0) {
          // Replace existing order
          setOrders(prev => {
            const newOrders = [...prev];
            newOrders[existingOrderIndex] = historyEntry;
            return newOrders;
          })
        } else {
          setOrders(prev => [historyEntry, ...prev])
        }
        
        setBarcode("")
        
        toast.success(t('scan.marked_pre_a_ramasse').replace('{code}', order.code))
        
      

      } else {
        throw new Error(response.data.message || t('scan.scan_error'))
      }

    } catch (error) {
      console.error("Scan error:", error)
      if (error.response?.status === 404) {
        new Audio(errorSound).play();
        toast.error(t('scan.not_found'))
        inputRef.current.value = "";
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message || t('scan.already_scanned'))
        inputRef.current.value = "";      
      } else {
        toast.error(t('scan.scan_error'))
        inputRef.current.value = "";
      }
    } finally {
      setLoading(false);

      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current.value = "";
      }, 2000);
    }
  }



  return (
    <div className="">
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6" style={{ background: "var(--color-gradient)" }} >
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <ScanLine className="w-6 h-6" />
                  {t('scan.scanner_title_ramassage')}
                </h2>
                <p className="text-white text-xs mt-1">
                  {t('scan.scanner_description_ramassage')}
                </p>
              </div>

              <div className="px-8 pb-8 pt-20 bg-white">
                <div className="space-y-4 ">
                  <BsQrCodeScan className="w-52 h-52 mx-auto dark:text-white" />
                  <div className="">
                    <input
                      ref={inputRef}
                      type="text"
                      value={barcode}
                      onChange={handleBarcodeChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          scanOrder(barcode)
                        }
                      }}
                      onBlur={() => inputRef.current?.focus()}
                      autoFocus
                      disabled={loading}
                      className="w-full px-4 py-3 mt-8 text-xs outline-none border-2 border-border rounded-lg  transition-colors disabled:bg-gray-50"
                    />
                    <p className="text-[10px] dark:text-white/60 text-muted-foreground mt-2 text-center">
                      {loading ? t('scan.scanning') : t('scan.press_enter')}
                    </p>
                  </div>
                </div>

                {scannedOrder && (
                  <div className="mt-8 space-y-6 animate-in fade-in slide-in-from-top duration-300">
                    <div className="border-t-2 border-border">
                      <div className="mb-5">
                        <div className="mt-5 p-4 bg-green-50 border-2 border-green-200 rounded-lg flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-700">
                              {t('scan.scan_success')}
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              {t('scan.current_status')} <span className="font-semibold">{getStatusText(scannedOrder.status)}</span>
                            </p>
                            <p className="text-xs text-green-600 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {t('scan.scanned_on')} {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        {t('scan.scanned_order')}
                      </h3>

                      <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                        {/* Order Header */}
                        <div className="flex items-start justify-between mb-6">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold bg-primary/10 text-primary px-3 py-1 rounded-full">
                                #{scannedOrder.code}
                              </span>
                              <span className="text-[10px] text-muted-foreground bg-gray-100 px-2 py-1 rounded">
                                {scannedOrder.code_api}
                              </span>
                            </div>
                            <div className={`mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(scannedOrder.status)}`}>
                              {getStatusIcon(scannedOrder.status)}
                              {getStatusText(scannedOrder.status)}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">{scannedOrder.total} DH</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(scannedOrder.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Client Info Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="space-y-2">
                            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {t('scan.client')}
                            </p>
                            <p className="text-sm font-semibold text-foreground">{scannedOrder.client?.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {scannedOrder.client?.phone}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {t('scan.delivery')}
                            </p>
                            <p className="text-sm font-semibold text-foreground">{scannedOrder.client?.city}</p>
                            <p className="text-xs text-muted-foreground truncate">{scannedOrder.client?.address}</p>
                          </div>
                        </div>
                        
                        {/* Seller Info */}
                        {scannedOrder.seller_info && (
                          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-1">{t('scan.vendor')}</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-foreground">{scannedOrder.seller_info.name}</p>
                                <p className="text-xs text-muted-foreground">{scannedOrder.seller_info.name_store}</p>
                              </div>
                              <p className="text-xs text-muted-foreground">{scannedOrder.seller_info.phone}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Products List */}
                        <div className="border-t border-gray-200 pt-6">
                          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-3 flex items-center justify-between">
                            <span>{t('scan.products')} ({scannedOrder.products?.length || 0})</span>
                            <span className="text-primary">{t('scan.total')} {scannedOrder.total} DH</span>
                          </p>
                          <div className="space-y-3">
                            {scannedOrder.products?.map((product, index) => (
                              <div key={index} className="flex items-center justify-between px-3 py-1.5 bg-white rounded-lg border">
                                <div className="pr-5 w-16 h-16">
                                {product.image_py ? (
                                    <img
                                      src={`${BASE_URL}/storage/${product.image_py}`}
                                      alt={product.name}
                                      fill
                                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center h-full">
                                      <svg className="h-6 w-6 text-[var(--color-primary-100)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-foreground">{product.name}</span>
                                    <span className="text-xs text-muted-foreground">x{product.quantity}</span>
                                  </div>
                                  {product.variants?.map((variant, vIndex) => (
                                    <div key={vIndex} className="flex items-center gap-2 mt-1">
                                      <span className="text-[10px] font-medium text-muted-foreground">{variant.type}:</span>
                                      {variant.values.map((value, valueIndex) => (
                                        <span key={valueIndex} className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded flex items-center gap-1">
                                          {value.name}
                                          {value.value && (
                                            <span 
                                              className="w-2 h-2 rounded-full border border-gray-300"
                                              style={{ backgroundColor: value.value }}
                                            />
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-primary">{product.total} DH</p>
                                  <p className="text-xs text-muted-foreground">{product.selling_price} DH/{t('scan.per_unit')}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Status History Timeline */}
                        <div className="border-t border-gray-200 pt-6 mt-6">
                          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide mb-4">
                            {t('scan.status_history')}
                          </p>
                          <div className="space-y-1">
                            {scannedOrder.statusHistorique?.map((history, index) => ( 
                              <div key={history.id} className="flex items-start gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`${getStatusColor(history.status)} p-0.5 pl-[3.5px] pr-0 rounded-full`}>
                                    {getStatusIcon(history.status)}
                                  </div>
                                  {index !== scannedOrder.statusHistorique.length - 1 && (
                                    <div className="w-px h-10 bg-gray-200" />
                                  )}
                                </div>

                                <div>
                                  <p className="font-medium text-sm">
                                    {getStatusText(history.status)}
                                  </p>
                                  {history.raison && (
                                    <p className="text-gray-500 text-xs">{t('scan.reason')} {history.raison}</p>
                                  )}
                                  {
                                    history.comment && (history.status === 'reporte' || history.status === 'Reporté par livreur') ?
                                    <p className="text-gray-500 text-[11px]">{t('scan.postponed_to')} {history.comment}</p>
                                    : history.comment !== null ?
                                    <p className="text-gray-500 text-[11px]">{t('scan.comment')} {history.comment}</p>
                                    :''
                                  }
                                  <p className="text-gray-500 text-[10px]">
                                    {new Date(history.created_at).toLocaleString('fr-FR')}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* History Sidebar */}
          <div className="bg-white rounded-2xl shadow-sm border border-border overflow-hidden flex flex-col">
            <div className="p-6" style={{ background: "var(--color-gradient)" }} >
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {t('scan.history_title')}
              </h3>
              <p className="text-white text-xs mt-1">
                {orders.length} {t('scan.history_subtitle')}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-border">
              {orders.length === 0 ? (
                <div className="p-8 text-center flex flex-col items-center justify-center h-full">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                    <Package className="w-6 h-6 text-gray-500" />
                  </div>
                  <p className="text-sm font-medium dark:text-white">{t('scan.no_scans')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('scan.no_scans_desc')}</p>
                </div>
              ) : (
                orders.map((order) => (
                  <div 
                    key={order.id}
                    className={`px-4 py-2 hover:bg-gray-50 transition ${
                      scannedOrder?.id === order.id ? 'border-b border-gray-200' : ''
                    }`}
                    onClick={() => {
                      setScannedOrder(order.fullOrder)
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-sm text-foreground flex items-center gap-2">
                          {order.code}
                          {scannedOrder?.id === order.id && (
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground font-mono">{order.code_api}</p>
                      </div>
                      <div>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                        <div className="text-right mt-1">
                          <p className="text-[10px] text-muted-foreground">{order.scannedDate}  {order.scannedTime}</p>
                        </div>
                      </div>
                    </div>
                    
                  
                    {/* Quick Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          scanOrder(order.code_api);
                        }}
                        className="text-[11px] cursor-pointer px-2 pb-1 pt-0.5 bg-primary/10 text-primary rounded hover:bg-primary/20 transition"
                      >
                        {t('scan.rescan')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(order.code_api);
                          toast.success(t('scan.code_copied'));
                        }}
                        className="text-[11px] cursor-pointer px-2 pb-1 pt-0.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                      >
                        {t('scan.copy_code')}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {orders.length > 0 && (
              <div className="border-t border-border p-4 bg-gray-50">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {orders.length} {t('scan.history_subtitle')}
                    </span>
                    {orders.some(order => order.status === 'pre_a_ramasse') && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] rounded-full">
                        {orders.filter(order => order.status === 'pre_a_ramasse').length} {t('scan.marked_as_received')}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {  
                        setOrders([]);
                        toast.success(t('scan.history_cleared'));
                    }}
                    className="text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                    disabled={orders.length === 0}
                  >
                    {t('scan.clear')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}